import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DeviceSessions from "@/components/DeviceSessions";
import { Eye, EyeOff, Lock } from "lucide-react";
import { toast } from "sonner";

function PasswordInput({ id, value, onChange, placeholder, dataTestId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative mt-1.5">
      <Input
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="off"
        className="border-af focus-visible:ring-terracotta pr-11"
        data-testid={dataTestId}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        data-testid={`${dataTestId}-toggle`}
        aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        className="absolute inset-y-0 right-0 w-10 flex items-center justify-center text-af-muted hover:text-terracotta transition-colors"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    company_name: user?.company_name || "",
  });
  const [saving, setSaving] = useState(false);

  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      localStorage.setItem("afrimeet_user", JSON.stringify(data));
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) {
      toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      toast.error("La confirmation ne correspond pas au nouveau mot de passe.");
      return;
    }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      toast.success("Mot de passe modifié avec succès");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impossible de modifier le mot de passe");
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2">Paramètres</div>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl">Profil & compte</h1>
        <p className="text-af-secondary mt-2">Gérez vos informations personnelles.</p>
      </div>

      <Card className="p-8 bg-af-card border-af rounded-md">
        <form onSubmit={onSave} className="space-y-5">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={user?.email || ""} disabled
              className="mt-1.5 border-af bg-af-subtle" data-testid="settings-email" />
          </div>
          <div>
            <Label htmlFor="name">Nom complet</Label>
            <Input id="name" value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              className="mt-1.5 border-af focus-visible:ring-terracotta" data-testid="settings-name-input" />
          </div>
          <div>
            <Label htmlFor="company">Entreprise</Label>
            <Input id="company" value={form.company_name}
              onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
              className="mt-1.5 border-af focus-visible:ring-terracotta" data-testid="settings-company-input" />
          </div>
          <div>
            <Label>Rôle</Label>
            <div className="mt-1.5 text-sm text-af-secondary px-3 py-2 bg-af-subtle border border-af rounded-md">
              {user?.role === "admin" ? "Administrateur" : user?.role === "manager" ? "Manager" : "Collaborateur"}
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="settings-save-btn"
            className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-11">
            {saving ? "Enregistrement…" : "Enregistrer les modifications"}
          </Button>
        </form>
      </Card>

      <Card className="p-8 bg-af-card border-af rounded-md mt-8" data-testid="password-card">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="h-4 w-4 text-terracotta" />
          <h2 className="font-display font-bold text-lg">Changer le mot de passe</h2>
        </div>
        <p className="text-sm text-af-secondary mb-5">
          Utilisez au moins 8 caractères. Évitez les mots de passe utilisés ailleurs.
        </p>
        <form onSubmit={onChangePassword} className="space-y-5">
          <div>
            <Label htmlFor="pwd-current">Mot de passe actuel</Label>
            <PasswordInput
              id="pwd-current"
              value={pwd.current}
              onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
              placeholder="••••••••"
              dataTestId="settings-pwd-current"
            />
          </div>
          <div>
            <Label htmlFor="pwd-next">Nouveau mot de passe</Label>
            <PasswordInput
              id="pwd-next"
              value={pwd.next}
              onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
              placeholder="Au moins 8 caractères"
              dataTestId="settings-pwd-next"
            />
          </div>
          <div>
            <Label htmlFor="pwd-confirm">Confirmer le nouveau mot de passe</Label>
            <PasswordInput
              id="pwd-confirm"
              value={pwd.confirm}
              onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
              placeholder="Retapez le nouveau mot de passe"
              dataTestId="settings-pwd-confirm"
            />
          </div>
          <Button
            type="submit"
            disabled={pwdSaving || !pwd.current || !pwd.next || !pwd.confirm}
            data-testid="settings-pwd-save-btn"
            className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-11"
          >
            {pwdSaving ? "Modification…" : "Modifier le mot de passe"}
          </Button>
        </form>
      </Card>

      <div className="mt-8">
        <DeviceSessions />
      </div>
    </div>
  );
}
